import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { 
  Clock, 
  MapPin, 
  Users, 
  BookOpen, 
  ArrowLeft,
  ArrowRight,
  PlayCircle,
  X,
  RefreshCw,
  Grid,
  User,
  School,

  AlertCircle,
} from 'lucide-react';
import api from '../../api';

const StudentTimetableModal = ({ isOpen, onClose, userId, title = "My Timetable" }) => {
  const [timetableData, setTimetableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState('master');
  const [currentTime, setCurrentTime] = useState(moment());
  const [weekStart, setWeekStart] = useState(moment().startOf('isoWeek'));
  const [selectedDay, setSelectedDay] = useState(null);

  const academicDayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const fetchTimetable = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const subdomain = window.location.hostname.split('.')[0];
      const response = await api.post('/api/student/timetable', {
        userId,
        subdomain,
        viewType,
        weekStartDate: weekStart.format('YYYY-MM-DD')
      });

      setTimetableData(response.data.data);
      
    } catch (err) {
      console.error('❌ Error fetching timetable:', err);
      setError(err.response?.data?.message || 'Failed to load timetable');
    } finally {
      setLoading(false);
    }
  }, [userId, viewType, weekStart]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchTimetable();
    }
  }, [isOpen, userId, viewType, weekStart, fetchTimetable]); // <-- Now fetchTimetable is stable

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const prevWeek = () => setWeekStart(s => moment(s).subtract(1, 'week'));
  const nextWeek = () => setWeekStart(s => moment(s).add(1, 'week'));
  const todayWeek = () => {
    setWeekStart(moment().startOf('isoWeek'));
    setSelectedDay(null);
  };



  // Enhanced type classification with proper icons and colors
  const getPeriodConfig = (period) => {
    const configs = {
      core: {
        color: 'bg-blue-50 border-blue-200',
        badge: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: '📚',
        label: 'Core'
      },
      lab: {
        color: 'bg-green-50 border-green-200',
        badge: 'bg-green-100 text-green-800 border-green-200',
        icon: '🔬',
        label: 'Lab'
      },
      elective: {
        color: 'bg-purple-50 border-purple-200',
        badge: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: '🎯',
        label: 'Elective'
      },
      break: {
        color: 'bg-yellow-50 border-yellow-200',
        badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: '☕',
        label: 'Break'
      },
      custom: {
        color: 'bg-orange-50 border-orange-200',
        badge: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: '📝',
        label: 'Special'
      },
      academic: {
        color: 'bg-red-50 border-red-200',
        badge: 'bg-red-100 text-red-800 border-red-200',
        icon: '🎉',
        label: 'Holiday'
      }
    };

    return configs[period?.category] || configs.core;
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTimeForDisplay = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes}`;
  };

  const isPeriodOngoing = (period, dayDateISO) => {
    if (!period?.timeSlot || !dayDateISO) return false;
    const now = currentTime.clone();
    const start = moment(`${dayDateISO} ${period.timeSlot.start}`, 'YYYY-MM-DD HH:mm');
    let end = moment(`${dayDateISO} ${period.timeSlot.end}`, 'YYYY-MM-DD HH:mm');
    if (!end.isAfter(start)) end.add(1, 'day');
    return now.isSameOrAfter(start) && now.isBefore(end);
  };

  const getCurrentPeriod = () => {
    if (!timetableData?.weeklyView) return null;
    const todayISO = currentTime.format('YYYY-MM-DD');
    const todayData = timetableData.weeklyView.find(day => day.dateISO === todayISO);
    if (!todayData) return null;
    return todayData.periods.find(period => isPeriodOngoing(period, todayISO));
  };

  // View-only Academic Timetable Grid (Master style)
  const AcademicTimetableView = () => {
    if (!timetableData?.weeklyView) return null;

    // Collect all unique time slots
    const allTimeSlots = new Set();
    timetableData.weeklyView.forEach(day => {
      (day.periods || []).forEach(period => {
        if (period?.timeSlot?.start && period?.timeSlot?.end) {
          allTimeSlots.add(`${period.timeSlot.start}-${period.timeSlot.end}`);
        }
      });
    });
    const timeSlots = Array.from(allTimeSlots)
      .map(timeKey => {
        const [start, end] = timeKey.split('-');
        return { start, end, key: timeKey };
      })
      .sort((a, b) => a.start.localeCompare(b.start));

    const currentDay = currentTime.format('dddd');

    return (
      <div className="space-y-6">
        {/* Timetable Grid */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="w-28 p-4 text-left text-sm font-semibold text-gray-900 border-b border-r bg-gray-100">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4" />
                    <span>Time / Day</span>
                  </div>
                </th>
                {academicDayOrder.map(day => (
                  <th
                    key={day}
                    className={`p-4 text-center text-sm font-semibold border-b ${
                      day === currentDay ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-1">
                      <span className="font-semibold">{day.substring(0, 3)}</span>
                      {day === currentDay && (
                        <span className="text-xs text-blue-500 font-normal px-2 py-1 bg-blue-100 rounded-full">
                          Today
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((timeSlot, timeIndex) => (
                <tr key={timeSlot.key} className={timeIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="w-28 p-3 text-sm text-gray-600 border-b border-r font-mono bg-gray-50">
                    <div className="text-center space-y-1">
                      <div className="font-semibold text-gray-900">{formatTimeForDisplay(timeSlot.start)}</div>
                      <div className="text-xs text-gray-400">to</div>
                      <div className="font-semibold text-gray-900">{formatTimeForDisplay(timeSlot.end)}</div>
                    </div>
                  </td>
                  {academicDayOrder.map(day => {
                    const dayData = timetableData.weeklyView.find(d => d.dayName === day);
                    const period = dayData?.periods?.find(
                      p => p?.timeSlot && `${p.timeSlot.start}-${p.timeSlot.end}` === timeSlot.key
                    );
                    const isOngoing = period && isPeriodOngoing(period, dayData?.dateISO);

                    return (
                      <td
                        key={day}
                        className={`p-2 border-b align-top min-w-[140px] ${day === currentDay ? 'bg-blue-25' : ''}`}
                      >
                        {period ? (
                          <div className={`rounded-lg border p-2 h-full ${getPeriodConfig(period).color} ${isOngoing ? 'ring-2 ring-green-500 ring-opacity-50 border-green-300' : ''}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span>{getPeriodConfig(period).icon}</span>
                              {period.category === 'elective' && period.groupName ? (
                                <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{period.groupName}</span>
                              ) : (
                                <span className={`text-xs px-2 py-1 rounded-full ${getPeriodConfig(period).badge}`}>{getPeriodConfig(period).label}</span>
                              )}
                            </div>
                            <div className="font-semibold text-xs text-gray-900 truncate">{period.displaySubject}</div>
                            {period.groupName && period.category === 'elective' && (
                              <div className="text-[10px] text-purple-700 bg-purple-50 rounded px-1 mt-1 mb-1">{period.groupName}</div>
                            )}
                            {period.faculty && (
                              <div className="text-xs text-gray-700 truncate mt-1"><User className="inline w-3 h-3 mr-1" />{period.faculty.name}</div>
                            )}
                            {period.room && period.category !== 'break' && period.category !== 'academic' && (
                              <div className="text-xs text-gray-500 truncate mt-1"><MapPin className="inline w-3 h-3 mr-1" />{period.room}</div>
                            )}
                          </div>
                        ) : (
                          <div className="min-h-20 flex items-center justify-center text-gray-300 text-sm">—</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <BookOpen className="w-4 h-4" />
            <span>Timetable Guide</span>
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { type: 'core', label: 'Core Subjects', icon: '📚' },
              { type: 'lab', label: 'Lab Sessions', icon: '🔬' },
              { type: 'elective', label: 'Elective Subjects', icon: '🎯' },
              { type: 'break', label: 'Breaks', icon: '☕' },
              { type: 'custom', label: 'Special Sessions', icon: '📝' },
              { type: 'academic', label: 'Academic Events', icon: '🎉' }
            ].map((item) => {
              const config = getPeriodConfig({ category: item.type });
              return (
                <div key={item.type} className={`flex items-center space-x-3 p-3 rounded-lg border ${config.color}`}>
                  <span className="text-lg">{item.icon}</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{item.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };



  // Current Period Indicator
  const CurrentPeriodIndicator = () => {
    const currentPeriod = getCurrentPeriod();

    if (!currentPeriod) return null;

    const config = getPeriodConfig(currentPeriod);

    return (
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <PlayCircle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Current Class</h3>
              <p className="text-green-100 text-sm">Happening now</p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="font-semibold text-lg flex items-center space-x-2">
              <span>{config.icon}</span>
              <span>{currentPeriod.displaySubject}</span>
            </div>
            <div className="text-green-100 text-sm">
              {formatTime(currentPeriod.timeSlot.start)} - {formatTime(currentPeriod.timeSlot.end)} 
              {currentPeriod.room && ` • ${currentPeriod.room}`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Day View Component
  const DayView = () => {
    if (!selectedDay) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSelectedDay(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedDay.dayName}</h2>
                <p className="text-gray-600">
                  {moment(selectedDay.dateISO).format('DD MMMM YYYY')}
                  {selectedDay.isToday && <span className="text-blue-600 ml-2 font-medium">• Today</span>}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Total Sessions</div>
              <div className="text-2xl font-bold text-gray-900">{selectedDay.periods.length}</div>
            </div>
          </div>
        </div>

        <CurrentPeriodIndicator />

        {/* Periods List */}
        <div className="space-y-4">
          {selectedDay.periods.length > 0 ? (
            selectedDay.periods
              .sort((a, b) => a.timeSlot.start.localeCompare(b.timeSlot.start))
              .map((period, idx) => (
              <DetailedPeriodCard key={idx} period={period} dayDateISO={selectedDay.dateISO} />
            ))
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <School className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Classes Scheduled</h3>
              <p className="text-gray-600">You have no classes scheduled for this day</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Detailed Period Card
  const DetailedPeriodCard = ({ period, dayDateISO }) => {
    const isOngoing = isPeriodOngoing(period, dayDateISO);
    const config = getPeriodConfig(period);
    const isSkipDay = period?.isSkipDay;

    if (isSkipDay) {
      return (
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl border-2 border-dashed border-red-300 p-6 text-center">
          <div className="text-4xl mb-4">🎉</div>
          <h3 className="text-xl font-bold text-red-800 mb-2">Holiday - {period.displaySubject}</h3>
          <p className="text-red-600 mb-4">No classes scheduled for today</p>
        </div>
      );
    }

    return (
      <div className={`bg-white rounded-xl border p-4 transition-all hover:shadow-sm ${config.color} ${
        isOngoing ? 'ring-2 ring-green-500 ring-opacity-50 border-green-300' : ''
      }`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            <div className={`p-2 rounded-lg ${config.badge}`}>
              <span className="text-lg">{config.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-sm leading-tight">
                {period.displaySubject}
              </h4>
              {period.subjectCode && (
                <p className="text-gray-600 text-xs mt-1">{period.subjectCode}</p>
              )}
            </div>
          </div>
          
          <div className="flex flex-col items-end space-y-2">
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${config.badge}`}>
              {config.label}
            </span>
            {isOngoing && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200 flex items-center space-x-1">
                <PlayCircle className="w-3 h-3" />
                <span>Live</span>
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span className="font-medium">{formatTime(period.timeSlot.start)} - {formatTime(period.timeSlot.end)}</span>
            </div>
            {period.room && (
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>{period.room}</span>
              </div>
            )}
          </div>

          {(period.faculty || period.labBatch || period.electiveGroup) && (
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              {period.faculty && (
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{period.faculty.name}</span>
                </div>
              )}
              {period.labBatch && (
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>Batch {period.labBatch}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            {timetableData?.student && (
              <p className="text-gray-600">
                {timetableData.student.name} • {timetableData.student.class}
                {timetableData.student.section && ` • Section ${timetableData.student.section}`}
                {timetableData.student.labBatch && ` • Lab Batch ${timetableData.student.labBatch}`}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between p-6 border-b bg-gray-50">
          <div className="flex items-center space-x-4">
            {/* View Toggle */}
            <div className="flex space-x-1 bg-white rounded-lg border p-1">
              <button
                onClick={() => setViewType('master')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  viewType === 'master'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Grid className="w-4 h-4" />
                  <span>Weekly View</span>
                </div>
              </button>
            </div>

            {/* Week Navigation */}
            {viewType === 'master' && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={prevWeek}
                  className="p-2 hover:bg-white rounded-lg border border-gray-300 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 text-gray-600" />
                </button>
                
                <button
                  onClick={todayWeek}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Today
                </button>
                
                <button
                  onClick={nextWeek}
                  className="p-2 hover:bg-white rounded-lg border border-gray-300 transition-colors"
                >
                  <ArrowRight className="w-4 h-4 text-gray-600" />
                </button>

                <div className="text-sm font-medium text-gray-900">
                  {timetableData?.weekInfo && 
                    `${moment(timetableData.weekInfo.startDate).format('DD MMM')} - ${moment(timetableData.weekInfo.endDate).format('DD MMM YYYY')}`
                  }
                </div>
              </div>
            )}
          </div>

          <button
            onClick={fetchTimetable}
            className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(95vh-200px)]">
          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">Loading your timetable...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Timetable</h3>
              <p className="text-red-600 mb-6">{error}</p>
              <button
                onClick={fetchTimetable}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {viewType === 'master' && (selectedDay ? <DayView /> : <AcademicTimetableView />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentTimetableModal;